/**
 * All editable site content lives here: contact details, figures, client
 * attributions, services, engagement models. Figures, quotes, and the selected
 * engagement are realistic placeholders so the site reads as finished; replace
 * them with approved copy before public launch. Contact details can be
 * overridden per environment through VITE_* variables (see .env.example).
 *
 * Positioning: procurement advisory for mid-market and enterprise
 * organisations. Public copy describes outcomes and engagement models, not
 * internal method. Keep operational detail out of this file.
 */

export const placeholders = {
  legalEntity: 'Ledgerline Procurement',
  mailingAddress: '230 Grant Avenue, San Francisco, CA 94108',
  callbackNumber: '+1 (510) 647-6003',
  contactEmail: 'blake@ledgerlineprocurement.com',
  privacyEmail: 'blake@ledgerlineprocurement.com',
  retentionPeriod: '36 months',
  responseSla: 'one business day',
}

export const hero = {
  eyebrow: 'Procurement advisory',
  title: 'Find the value in your supply base. Then capture it.',
  lede:
    'Ledgerline Procurement works with mid-market and enterprise organisations to identify value at stake across their spend, run sourcing that covers the whole supplier market, and negotiate outcomes that hold through the life of the contract.',
  primaryCta: 'Speak with us',
  secondaryCta: 'How we work with clients',
}

export const figures = [
  { value: '$1.4B', label: 'Spend under review', note: 'across client engagements since 2024' },
  { value: '9–14%', label: 'Savings on competed categories', note: 'realised, not identified, against incumbent pricing' },
  { value: '3×', label: 'Supplier market coverage', note: 'compared with a conventional RFP long list' },
  { value: '60+', label: 'Categories sourced', note: 'indirect and direct, services and goods' },
]

export const services = [
  {
    name: 'Spend diagnostic and value at stake',
    what: 'A structured review of spend, contracts, and supplier concentration to establish where value is available, how much, and in what order to pursue it. Delivered with the analysis behind every estimate.',
    outcome: 'A prioritised opportunity portfolio your leadership can commit to.',
  },
  {
    name: 'Strategic sourcing with full market coverage',
    what: 'Sourcing events that reach the entire qualified supplier market for a requirement, including the regional and specialist suppliers conventional processes leave out. Technology-enabled coverage lets us run these at a scale and pace manual teams cannot match.',
    outcome: 'A qualified, documented supplier field and a defensible shortlist.',
  },
  {
    name: 'Evaluation and decision support',
    what: 'Normalised total cost, benchmarked incumbents, and a clear view of trade-offs, prepared for the stakeholders who own the decision. We facilitate the deliberation and keep it moving.',
    outcome: 'A decision made on comparable evidence, with the record to stand behind it.',
  },
  {
    name: 'Negotiation and contracting',
    what: 'We lead or support commercial negotiation with the shortlist, using the market evidence we gathered to secure price, terms, service levels, and exit conditions that protect you at renewal.',
    outcome: 'Executed agreements at or below benchmark and a savings baseline to track realisation against.',
  },
]

export const sectors = [
  'Industrial manufacturing',
  'Healthcare systems',
  'Food and consumer distribution',
  'Commercial real estate and facilities',
  'Logistics and transportation',
  'Financial and professional services',
]

export const categories = [
  'Facilities and MRO services',
  'Logistics and freight',
  'Packaging and industrial supplies',
  'Field service and operations software',
  'Contingent labour and professional services',
  'IT, telecom, and managed services',
  'Corporate services and travel',
  'Energy and utilities',
]

export const principles = [
  {
    name: 'Coverage before convenience',
    text: 'The best supplier for a requirement is often one nobody on the buying team has heard of. We compete the whole market, not the familiar names.',
  },
  {
    name: 'Evidence before opinion',
    text: 'Every figure in our work traces to a quote, a price list, or a contract clause. Recommendations are built on the record, not on relationships.',
  },
  {
    name: 'Independence',
    text: 'We are paid by clients only. No supplier commissions, referral fees, or preferred lists. Every requirement is competed on its merits.',
  },
]

export const engagementModels = [
  {
    name: 'Value diagnostic',
    who: 'For organisations that want to know where to start.',
    what: 'Spend, contract, and supplier analysis across the categories in scope, with stakeholder interviews to understand requirements and constraints.',
    deliverable: 'Value-at-stake assessment: addressable spend, savings range, risk, and a sequenced programme.',
  },
  {
    name: 'Category sourcing programme',
    who: 'For a defined set of requirements with meaningful spend.',
    what: 'End-to-end sourcing for each requirement: market coverage, supplier qualification, evaluation, and negotiation, with your team engaged at each decision point.',
    deliverable: 'Executed agreements, a documented supplier field, and a savings baseline.',
  },
  {
    name: 'Negotiation support',
    who: 'For renewals and awards already in motion.',
    what: 'Market benchmarking and negotiation strategy, with Ledgerline at the table or behind it as you prefer.',
    deliverable: 'Improved commercial terms and a negotiation record for the next cycle.',
  },
  {
    name: 'Ongoing category management',
    who: 'For clients who want the discipline to persist.',
    what: 'Renewal calendar, periodic market checks, supplier performance reviews, and savings realisation tracking across the categories we have sourced.',
    deliverable: 'Quarterly category reviews and realised-savings reporting.',
  },
]

export const engagementArc = {
  eyebrow: 'How an engagement runs',
  title: 'Diagnose, cover the market, decide, negotiate',
  text:
    'Engagements begin with a diagnostic that establishes value at stake and the order of attack. For each requirement we then build and qualify the full supplier field, bring normalised evidence to your stakeholders for deliberation, and negotiate on your behalf. Your team keeps every decision and every signature; we keep the process rigorous and the record complete.',
}

export const testimonials = [
  {
    quote:
      'They found nineteen qualified suppliers for a category where our team had three on the list. Two of the finalists were firms we had never spoken to, and one of them now holds the contract.',
    name: 'Chief Procurement Officer',
    company: 'Multi-site industrial manufacturer',
  },
  {
    quote:
      'The deliberation pack ended a debate that had run for two quarters. Every number had a source, so the conversation moved from opinions to a decision.',
    name: 'Vice President, Supply Chain',
    company: 'Regional healthcare system',
  },
  {
    quote:
      'Our incumbent renewal came in eleven percent lower once the market evidence was on the table. We would not have had that leverage on our own.',
    name: 'Chief Financial Officer',
    company: 'Food and consumer distributor',
  },
]

export const sampleEngagement = {
  eyebrow: 'Selected engagement',
  title: 'Facilities services consolidation for a 60-site manufacturer',
  summary:
    'An industrial manufacturer with sixty sites across four regions was buying mechanical maintenance, janitorial, and grounds services from forty-seven suppliers under legacy local arrangements. Leadership wanted consistent service levels and a defensible cost position without disrupting operations.',
  rows: [
    ['Addressable spend', '$38M annually'],
    ['Suppliers in the market', '190 qualified'],
    ['Shortlisted', '9'],
    ['Awarded', '6 regional providers'],
    ['Realised savings', '12% year one'],
    ['Programme duration', '14 weeks'],
  ],
  result:
    'Service levels were standardised across all sixty sites, supplier count fell from forty-seven to six, and the savings baseline is tracked quarterly against invoices.',
}

export const method = {
  eyebrow: 'Supplier engagement',
  title: 'How suppliers experience a Ledgerline process',
  lede:
    'Suppliers hear from us by email or phone on behalf of an anonymised client, take part in a structured conversation about fit and commercial terms, and can provide materials in writing. Every supplier is treated to the same process, and every answer is recorded with its source.',
  facts: [
    { eyebrow: 'Contact', value: 'Email or phone', note: 'A clear description of the requirement, a callback line, and a named analyst to reply to.' },
    { eyebrow: 'Conversation', value: 'Structured and consistent', note: 'The same questions for every supplier, so answers compare cleanly.' },
    { eyebrow: 'Record', value: 'Documented and reviewed', note: 'Answers are recorded with their source and reviewed by an analyst before they reach the client.' },
  ],
}

export const processSteps = [
  {
    name: 'Define',
    detail: 'Requirements, evaluation criteria, and commercial expectations are agreed with your stakeholders before any supplier is approached.',
  },
  {
    name: 'Cover the market',
    detail: 'We identify and engage the full field of qualified suppliers for the requirement, not a shortlist of the familiar.',
  },
  {
    name: 'Qualify and evaluate',
    detail: 'Suppliers are screened against your criteria, costs normalised to a common basis, and the incumbent benchmarked.',
  },
  {
    name: 'Decide and negotiate',
    detail: 'Your stakeholders deliberate on comparable evidence; we negotiate with the shortlist on your behalf and document the outcome.',
  },
]
